// Implements: F-PRV-002, F-PRV-007
/**
 * The viewer as the mockup page mounts it: `PreviewContainer` fed with the mockup's current version
 * and its source.
 *
 * The page keys this container by mockup (checklist 14), so nothing a viewer remembers leaks to the
 * next mockup. Nothing is rendered until the mockup has been read, and nothing for a mockup the
 * server refused (the rest of the page reports that); afterwards the frame loads the current
 * version by `src` from the authenticated frame route, and the empty state shows while the mockup
 * has no version.
 */

import type { ReactElement, ReactNode } from "react";

import { useMockupPreview } from "../hooks/useMockupPreview";
import { PreviewContainer } from "./PreviewContainer";

export interface MockupPreviewContainerProps {
  readonly mockupId: string;
  /** Another version than the current one to show (the version chip's preview), or `null`. */
  readonly showVersionId?: string | null | undefined;
  /** Drawn over the canvas (the banner of a previewed version). */
  readonly banner?: ReactNode;
  /** The share control, after the tabs. */
  readonly share?: ReactNode;
}

export function MockupPreviewContainer({
  mockupId,
  showVersionId = null,
  banner,
  share,
}: MockupPreviewContainerProps): ReactElement | null {
  const preview = useMockupPreview(mockupId, showVersionId);
  if (preview.status === "unavailable") return null;
  return (
    <PreviewContainer
      mockupId={mockupId}
      versionId={preview.versionId}
      source={preview.source}
      sourceStatus={preview.sourceStatus}
      onSourceRetry={preview.retrySource}
      banner={banner}
      share={share}
    />
  );
}
